import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Pencil, Trash2, Plus, X, Search, Receipt, CalendarPlus, ArrowUp, ArrowDown, Upload } from "lucide-react";
import api, { fmtErr } from "../../lib/api";
import ClientsImportModal from "./ClientsImportModal";

const emptyForm = () => ({ name: "", email: "", phone: "", region: "", birthday: "" });

const fmtBirthday = (b) => {
  if (!b) return "—";
  const parts = b.split("-");
  if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
  return b;
};

const fmtCreated = (c) => {
  if (!c) return "—";
  const d = new Date(c);
  if (isNaN(d)) return "—";
  return d.toLocaleDateString("pt-PT", { day: "2-digit", month: "2-digit", year: "numeric" });
};

const COLS = [
  { key: "name", label: "Nome" },
  { key: "email", label: "Email" },
  { key: "phone", label: "Telefone" },
  { key: "region", label: "Região" },
  { key: "birthday", label: "Aniversário" },
  { key: "created_at", label: "Criado" },
];

export const ClientsAdmin = ({ onStartSale, onStartBooking }) => {
  const [clients, setClients] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState({ key: "name", dir: "asc" });
  const [showImport, setShowImport] = useState(false);

  const toggleSort = (key) =>
    setSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }));

  const sorted = useMemo(() => {
    const dir = sort.dir === "asc" ? 1 : -1;
    return [...clients].sort((a, b) => {
      const av = a[sort.key] == null ? "" : String(a[sort.key]);
      const bv = b[sort.key] == null ? "" : String(b[sort.key]);
      if (av === bv) return 0;
      if (av === "") return 1; // vazios no fim
      if (bv === "") return -1;
      return av.localeCompare(bv, "pt", { numeric: true, sensitivity: "base" }) * dir;
    });
  }, [clients, sort]);

  const load = useCallback(() => {
    setLoading(true);
    api
      .get("/admin/clients/manage", { params: q ? { q } : {} })
      .then((res) => {
        setClients(res.data.clients || []);
        setTotal(res.data.total || 0);
      })
      .finally(() => setLoading(false));
  }, [q]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const openNew = () => {
    setEditing(null);
    setForm(emptyForm());
    setShowForm(true);
  };

  const openEdit = (c) => {
    setEditing(c.id);
    setForm({
      name: c.name || "",
      email: c.email || "",
      phone: c.phone || "",
      region: c.region || "",
      birthday: c.birthday || "",
    });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("O nome é obrigatório.");
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/admin/clients/manage/${editing}`, form);
        toast.success("Cliente atualizado.");
      } else {
        await api.post("/admin/clients/manage", form);
        toast.success("Cliente criado.");
      }
      setShowForm(false);
      load();
    } catch (err) {
      toast.error(fmtErr(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Eliminar este cliente?")) return;
    try {
      await api.delete(`/admin/clients/manage/${id}`);
      toast.success("Cliente eliminado.");
      load();
    } catch (err) {
      toast.error(fmtErr(err.response?.data?.detail));
    }
  };

  const inputCls =
    "w-full bg-white border border-black/15 px-3 py-2.5 text-sm font-light focus:outline-none focus:border-clay";

  return (
    <div className="space-y-6" data-testid="clients-admin">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="relative">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-black/30" />
          <input
            data-testid="clients-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Pesquisar por nome, email, telefone…"
            className="bg-white border border-black/15 pl-9 pr-3 py-2.5 text-sm font-light focus:outline-none focus:border-clay w-72 max-w-full"
          />
        </div>
        <div className="flex items-center gap-5">
          <p className="text-sm text-black/40 font-light">{total} cliente{total === 1 ? "" : "s"}</p>
          <button
            data-testid="clients-import-btn"
            onClick={() => setShowImport(true)}
            className="label-editorial border border-clay text-clay px-6 py-3.5 hover:bg-clay hover:text-white transition-colors flex items-center gap-2"
          >
            <Upload size={14} /> Importar
          </button>
          <button
            data-testid="client-add-btn"
            onClick={openNew}
            className="label-editorial bg-clay text-white px-6 py-3.5 hover:bg-clay-dark transition-colors flex items-center gap-2"
          >
            <Plus size={14} /> Novo Cliente
          </button>
        </div>
      </div>

      <div className="bg-white border border-black/5 overflow-x-auto">
        <table data-testid="clients-table" className="w-full text-sm font-light min-w-[920px]">
          <thead>
            <tr className="text-left text-xs uppercase tracking-widest text-black/40 border-b border-black/10">
              {COLS.map((c) => (
                <th key={c.key} className="p-4">
                  <button
                    type="button"
                    data-testid={`clients-sort-${c.key}`}
                    onClick={() => toggleSort(c.key)}
                    className={`inline-flex items-center gap-1 uppercase tracking-widest hover:text-black transition-colors ${sort.key === c.key ? "text-clay" : ""}`}
                  >
                    {c.label}
                    {sort.key === c.key && (sort.dir === "asc" ? <ArrowUp size={12} /> : <ArrowDown size={12} />)}
                  </button>
                </th>
              ))}
              <th className="p-4"></th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={7} className="p-6 text-black/30">A carregar…</td></tr>
            )}
            {!loading && sorted.length === 0 && (
              <tr><td colSpan={7} className="p-6 text-black/30">Sem clientes. Crie o primeiro com o botão «Novo Cliente».</td></tr>
            )}
            {!loading && sorted.map((c) => (
              <tr key={c.id || c.name} data-testid={`client-row-${c.id}`} className="border-b border-black/5 last:border-0 hover:bg-black/[0.015]">
                <td className="p-4 font-medium">{c.name}</td>
                <td className="p-4 text-black/60">{c.email || "—"}</td>
                <td className="p-4 text-black/60 whitespace-nowrap">{c.phone || "—"}</td>
                <td className="p-4 text-black/60">{c.region || "—"}</td>
                <td className="p-4 text-black/60 whitespace-nowrap">{fmtBirthday(c.birthday)}</td>
                <td className="p-4 text-black/50 whitespace-nowrap">{fmtCreated(c.created_at)}</td>
                <td className="p-4 whitespace-nowrap text-right">
                  <div className="inline-flex items-center gap-1">
                    <button
                      data-testid={`client-booking-${c.id}`}
                      onClick={() => onStartBooking && onStartBooking(c)}
                      className="p-1.5 hover:bg-clay/10 text-clay"
                      title="Iniciar marcação"
                      aria-label="Iniciar marcação"
                    >
                      <CalendarPlus size={15} />
                    </button>
                    <button
                      data-testid={`client-sale-${c.id}`}
                      onClick={() => onStartSale && onStartSale(c)}
                      className="p-1.5 hover:bg-clay/10 text-clay"
                      title="Registar venda (caixa)"
                      aria-label="Registar venda"
                    >
                      <Receipt size={15} />
                    </button>
                    {c.id && (
                      <>
                        <button data-testid={`client-edit-${c.id}`} onClick={() => openEdit(c)} className="p-1.5 hover:bg-black/5 text-black/60" aria-label="Editar">
                          <Pencil size={15} />
                        </button>
                        <button data-testid={`client-delete-${c.id}`} onClick={() => handleDelete(c.id)} className="p-1.5 hover:bg-black/5 text-red-600" aria-label="Eliminar">
                          <Trash2 size={15} />
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showForm && (
        <div
          data-testid="client-form-overlay"
          className="fixed inset-0 z-[80] flex items-start md:items-center justify-center p-4 bg-black/50 animate-fade-in overflow-y-auto"
          onClick={() => setShowForm(false)}
        >
          <form
            onSubmit={handleSubmit}
            data-testid="client-form"
            onClick={(e) => e.stopPropagation()}
            className="bg-white border border-black/10 p-6 md:p-8 space-y-5 w-full max-w-xl my-8 animate-pop-in shadow-xl"
          >
            <div className="flex items-center justify-between">
              <p className="label-editorial">{editing ? "Editar cliente" : "Novo cliente"}</p>
              <button type="button" onClick={() => setShowForm(false)} data-testid="client-form-close" className="text-black/40 hover:text-black">
                <X size={18} />
              </button>
            </div>
            <div>
              <label className="label-editorial text-black/50 block mb-2">Nome *</label>
              <input data-testid="client-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Nome do cliente" className={inputCls} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="label-editorial text-black/50 block mb-2">Email</label>
                <input data-testid="client-email-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="email@exemplo.pt" className={inputCls} />
              </div>
              <div>
                <label className="label-editorial text-black/50 block mb-2">Telefone</label>
                <input data-testid="client-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="912 345 678" className={inputCls} />
              </div>
              <div>
                <label className="label-editorial text-black/50 block mb-2">Região</label>
                <input data-testid="client-region-input" value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })} placeholder="Ex.: Leiria" className={inputCls} />
              </div>
              <div>
                <label className="label-editorial text-black/50 block mb-2">Data de aniversário</label>
                <input data-testid="client-birthday-input" type="date" value={form.birthday} onChange={(e) => setForm({ ...form, birthday: e.target.value })} className={inputCls} />
              </div>
            </div>
            <div className="flex items-center gap-4 pt-1">
              <div className="flex-1" />
              <button type="button" onClick={() => setShowForm(false)} className="text-sm text-black/50 hover:text-black">Cancelar</button>
              <button data-testid="client-submit-btn" type="submit" disabled={saving} className="label-editorial bg-clay text-white px-8 py-3 hover:bg-clay-dark transition-colors disabled:opacity-50">
                {saving ? "A guardar…" : editing ? "Guardar" : "Criar Cliente"}
              </button>
            </div>
          </form>
        </div>
      )}

      {showImport && (
        <ClientsImportModal
          onClose={() => setShowImport(false)}
          onImported={load}
        />
      )}
    </div>
  );
};
